import * as pc from 'playcanvas';
import { matCache } from '../utils/MaterialCache';

/**
 * Palette for remote machines — green is reserved for the local player.
 * Colours cycle with mod so the game handles any number of players.
 */
export const PLAYER_COLORS: [number, number, number][] = [
  [1.00, 0.13, 0.13], //  0 red
  [1.00, 0.40, 0.00], //  1 orange
  [1.00, 0.80, 0.00], //  2 yellow
  [0.00, 0.80, 1.00], //  3 cyan
  [0.67, 0.27, 1.00], //  4 purple
  [1.00, 0.27, 0.67], //  5 hot pink
  [0.27, 0.53, 1.00], //  6 cornflower blue
  [0.27, 0.87, 0.73], //  7 teal
  [0.10, 0.85, 0.35], //  8 lime green
  [1.00, 0.60, 0.80], //  9 rose
  [0.55, 0.27, 1.00], // 10 violet
  [1.00, 0.55, 0.00], // 11 amber
  [0.00, 0.70, 0.50], // 12 emerald
  [0.90, 0.90, 0.90], // 13 white
  [1.00, 0.27, 0.27], // 14 coral
  [0.00, 0.47, 1.00], // 15 azure
  [0.80, 1.00, 0.00], // 16 chartreuse
  [1.00, 0.80, 0.60], // 17 peach
  [0.60, 0.80, 1.00], // 18 sky
  [0.80, 0.40, 0.00], // 19 bronze
  [0.50, 1.00, 0.70], // 20 mint
  [1.00, 0.00, 0.50], // 21 magenta
  [0.00, 0.90, 0.90], // 22 aqua
  [0.90, 0.70, 0.00], // 23 gold
];

export interface MachineVisualState {
  x: number;
  z: number;
  rotation: number;
  turretAngle: number;
  hp: number;
  maxHp: number;
  level: number;
  weaponTier: number;
  chassisTier: number;
  movementTier: number;
  attachment: string | null;
  name: string;
}

export class MachineEntity {
  root: pc.Entity;
  private body: pc.Entity;
  private chassisVisuals: pc.Entity; // chassis meshes only; cleared by buildChassis so turret/movement stay
  private turretBase: pc.Entity;
  private barrel: pc.Entity;
  private hpBar: pc.Entity | null = null;
  private hpBarFill: pc.Entity | null = null;
  public isLocal: boolean;
  private colorIndex: number;

  // One entry per physical barrel — world-space tip positions used for bullet spawning
  private muzzleMarkers: pc.Entity[] = [];
  private _bodyRotationRad = 0;

  private currentWeaponTier = -1;
  private currentChassisTier = -1;
  private currentMovementTier = -1;
  private frozenOverlay: pc.Entity | null = null;

  constructor(app: pc.Application, isLocal: boolean, name: string, colorIndex = 0) {
    this.isLocal = isLocal;
    this.colorIndex = colorIndex;

    // Root entity
    this.root = new pc.Entity(`machine_${name}`);
    app.root.addChild(this.root);

    // Body — carries rotation; chassis, turret and movement visuals are under it
    this.body = new pc.Entity('body');
    this.root.addChild(this.body);

    // Chassis container — buildChassis only clears this, so turret/movement are not destroyed
    this.chassisVisuals = new pc.Entity('chassisVisuals');
    this.body.addChild(this.chassisVisuals);

    // Turret base sits on top of body (child of body so it turns with the body)
    this.turretBase = new pc.Entity('turretBase');
    this.turretBase.setLocalPosition(0, 0.6, 0);
    this.body.addChild(this.turretBase);

    // Barrel container — always at (0,0,0) relative to turretBase
    this.barrel = new pc.Entity('barrel');
    this.turretBase.addChild(this.barrel);

    // Build initial visuals
    this.buildChassis(0);
    this.buildWeapon(0);
    this.buildMovement(0);

    // Name tag for remote players
    if (!isLocal) {
      this.buildNameTag(name);
    }

    this.buildHpBar();

    // Frozen aura — enabled via setFrozen()
    const frozenOverlay = new pc.Entity('frozenOverlay');
    frozenOverlay.addComponent('render', { type: 'sphere' });
    frozenOverlay.render!.meshInstances[0].material = matCache.get({
      diffuse: new pc.Color(0.3, 0.75, 1.0),
      emissive: new pc.Color(0.3, 0.75, 1.0),
      emissiveIntensity: 1.8,
      blendType: pc.BLEND_ADDITIVE,
      depthWrite: false,
    });
    frozenOverlay.render!.castShadows = false;
    frozenOverlay.render!.receiveShadows = false;
    frozenOverlay.setLocalScale(2.6, 2.6, 2.6);
    frozenOverlay.setLocalPosition(0, 0.5, 0);
    frozenOverlay.enabled = false;
    this.body.addChild(frozenOverlay);
    this.frozenOverlay = frozenOverlay;
  }

  private clearChildren(entity: pc.Entity) {
    while (entity.children.length > 0) {
      const child = entity.children[0] as pc.Entity;
      child.destroy();
    }
    if (entity.render) entity.removeComponent('render');
  }

  buildChassis(tier: number) {
    if (tier === this.currentChassisTier) return;
    this.currentChassisTier = tier;
    this.clearChildren(this.chassisVisuals);
    if (this.chassisVisuals.render) this.chassisVisuals.removeComponent('render');

    const [r, g, b] = PLAYER_COLORS[this.colorIndex % PLAYER_COLORS.length];
    const baseColor = new pc.Color(r, g, b);

    switch (tier) {
      case 0: { // Dome
        this.chassisVisuals.addComponent('render', { type: 'sphere' });
        this.chassisVisuals.render!.meshInstances[0].material = matCache.get({ diffuse: baseColor });
        this.chassisVisuals.setLocalScale(1.4, 1.0, 1.4);
        break;
      }
      case 1: { // Armored hexagonal - approximate with box + chamfer feel
        this.chassisVisuals.addComponent('render', { type: 'box' });
        this.chassisVisuals.render!.meshInstances[0].material = matCache.get({ diffuse: baseColor });
        this.chassisVisuals.setLocalScale(1.5, 0.9, 1.5);

        // Add panel details
        const panelCol = new pc.Color(baseColor.r * 0.7, baseColor.g * 0.7, baseColor.b * 0.7);
        for (let i = 0; i < 4; i++) {
          const panel = new pc.Entity('panel');
          panel.addComponent('render', { type: 'box' });
          panel.render!.meshInstances[0].material = matCache.get({ diffuse: panelCol });
          panel.setLocalScale(0.5, 0.12, 0.5);
          const angle = (i / 4) * Math.PI * 2;
          panel.setLocalPosition(Math.cos(angle) * 0.5, 0.5, Math.sin(angle) * 0.5);
          this.chassisVisuals.addChild(panel);
        }
        break;
      }
      case 2: { // Spiked Hull
        this.chassisVisuals.addComponent('render', { type: 'sphere' });
        this.chassisVisuals.render!.meshInstances[0].material = matCache.get({ diffuse: baseColor });
        this.chassisVisuals.setLocalScale(1.35, 0.95, 1.35);

        // Spikes
        const spikeCol = new pc.Color(0.8, 0.8, 0.1);
        for (let i = 0; i < 6; i++) {
          const spike = new pc.Entity('spike');
          spike.addComponent('render', { type: 'cone' });
          spike.render!.meshInstances[0].material = matCache.get({ diffuse: spikeCol, emissive: spikeCol, emissiveIntensity: 0.4 });
          spike.setLocalScale(0.14, 0.45, 0.14);
          const angle = (i / 6) * Math.PI * 2;
          spike.setLocalPosition(Math.cos(angle) * 0.75, 0.3, Math.sin(angle) * 0.75);
          spike.setLocalEulerAngles(0, 0, 90 - (angle * 180 / Math.PI));
          this.chassisVisuals.addChild(spike);
        }
        break;
      }
      case 3: { // Reactive Armor - glowing rim
        this.chassisVisuals.addComponent('render', { type: 'sphere' });
        this.chassisVisuals.render!.meshInstances[0].material = matCache.get({ diffuse: baseColor });
        this.chassisVisuals.setLocalScale(1.5, 1.05, 1.5);

        const ring = new pc.Entity('ring');
        ring.addComponent('render', { type: 'cylinder' });
        ring.render!.meshInstances[0].material = matCache.get({
          diffuse: new pc.Color(0.2, 0.6, 1),
          emissive: new pc.Color(0.2, 0.6, 1),
          emissiveIntensity: 1.2,
        });
        ring.setLocalScale(1.8, 0.08, 1.8);
        ring.setLocalPosition(0, -0.2, 0);
        this.chassisVisuals.addChild(ring);
        break;
      }
    }
    this.chassisVisuals.render?.meshInstances.forEach(mi => (mi.material as pc.StandardMaterial).update());
  }

  buildWeapon(tier: number) {
    if (tier === this.currentWeaponTier) return;
    this.currentWeaponTier = tier;

    // Destroy old barrel children (this also destroys old muzzle marker entities)
    this.clearChildren(this.barrel);
    this.muzzleMarkers = [];

    // Reset barrel container transform
    this.barrel.setLocalPosition(0, 0, 0);
    this.barrel.setLocalScale(1, 1, 1);
    this.barrel.setLocalEulerAngles(0, 0, 0);

    // Turret dome cap
    if (!this.turretBase.render) {
      this.turretBase.addComponent('render', { type: 'sphere' });
      this.turretBase.render!.meshInstances[0].material = matCache.get({ diffuse: new pc.Color(0.2, 0.2, 0.25) });
      this.turretBase.setLocalScale(0.7, 0.45, 0.7);
    }

    // Helper: add one barrel cylinder + muzzle marker.
    // Cylinders default to Y-axis (vertical). setLocalEulerAngles(-90,0,0) rotates
    // the cylinder so its length axis aligns with the turret's local -Z (forward).
    // With that rotation applied:
    //   cylinder center at (x, y, -halfLen) → rear flush with turret, tip at (x, y, -fullLen).
    const addBarrel = (xOffset: number, yHeight: number, len: number, radius: number, mat: pc.StandardMaterial) => {
      const half = len / 2;

      const cyl = new pc.Entity('barrel_cyl');
      cyl.addComponent('render', { type: 'cylinder' });
      (cyl.render!.meshInstances[0].material as pc.StandardMaterial) = mat;
      cyl.setLocalScale(radius, len, radius);
      cyl.setLocalEulerAngles(-90, 0, 0);   // stand it horizontal, pointing -Z
      cyl.setLocalPosition(xOffset, yHeight, -half);
      this.barrel.addChild(cyl);

      // Invisible entity at the barrel tip — used for world-space muzzle queries
      const marker = new pc.Entity('muzzle');
      marker.setLocalPosition(xOffset, yHeight, -len);
      this.barrel.addChild(marker);
      this.muzzleMarkers.push(marker);
    };

    switch (tier) {
      case 0: { // Single barrel
        addBarrel(0, 0.1, 0.9, 0.18, matCache.get({ diffuse: new pc.Color(0.25, 0.25, 0.3) }));
        break;
      }
      case 1: { // Double barrel
        const mat = matCache.get({ diffuse: new pc.Color(0.25, 0.25, 0.3) });
        addBarrel(-0.18, 0.1, 0.85, 0.14, mat);
        addBarrel( 0.18, 0.1, 0.85, 0.14, mat);
        break;
      }
      case 2: { // Shotgun — 3 short barrels
        const mat = matCache.get({ diffuse: new pc.Color(0.3, 0.25, 0.2) });
        for (const ox of [-0.22, 0, 0.22]) {
          addBarrel(ox, 0.1, 0.6, 0.16, mat);
        }
        break;
      }
      case 3: { // Sniper — long thin barrel
        addBarrel(0, 0.1, 1.6, 0.12, matCache.get({ diffuse: new pc.Color(0.15, 0.15, 0.2) }));
        break;
      }
      case 4: { // Mortar — stubby wide barrel
        addBarrel(0, 0.1, 0.55, 0.35, matCache.get({ diffuse: new pc.Color(0.3, 0.2, 0.1) }));
        break;
      }
      case 5: { // Laser — glowing emitter sphere at tip
        const emitter = new pc.Entity('emitter');
        emitter.addComponent('render', { type: 'sphere' });
        emitter.render!.meshInstances[0].material = matCache.get({
          diffuse: new pc.Color(0.1, 0.8, 1),
          emissive: new pc.Color(0.1, 0.8, 1),
          emissiveIntensity: 2.5,
          blendType: pc.BLEND_ADDITIVE,
          depthWrite: false,
        });
        emitter.setLocalScale(0.28, 0.28, 0.28);
        emitter.setLocalPosition(0, 0.1, -0.5);
        this.barrel.addChild(emitter);

        const marker = new pc.Entity('muzzle');
        marker.setLocalPosition(0, 0.1, -0.5);
        this.barrel.addChild(marker);
        this.muzzleMarkers.push(marker);
        break;
      }
    }
  }

  buildMovement(tier: number) {
    if (tier === this.currentMovementTier) return;
    this.currentMovementTier = tier;

    // Remove previous movement visuals
    const existing = this.root.findByName('movement_base') as pc.Entity | null;
    existing?.destroy();

    const base = new pc.Entity('movement_base');
    this.body.addChild(base);

    switch (tier) {
      case 0: { // Hover - subtle disc
        base.addComponent('render', { type: 'cylinder' });
        base.render!.meshInstances[0].material = matCache.get({
          diffuse: new pc.Color(0.15, 0.15, 0.2),
          emissive: new pc.Color(0.15, 0.15, 0.2),
          emissiveIntensity: 0.6,
        });
        base.setLocalScale(1.0, 0.07, 1.0);
        base.setLocalPosition(0, -0.55, 0);
        break;
      }
      case 1: { // Tank treads
        const treadMat = matCache.get({ diffuse: new pc.Color(0.2, 0.2, 0.2) });
        for (let side = -1; side <= 1; side += 2) {
          const tread = new pc.Entity('tread');
          tread.addComponent('render', { type: 'box' });
          tread.render!.meshInstances[0].material = treadMat;
          tread.setLocalScale(0.35, 0.28, 1.6);
          tread.setLocalPosition(side * 0.8, -0.55, 0);
          base.addChild(tread);
        }
        break;
      }
      case 2: { // Boosters - rear rockets
        const boosterMat = matCache.get({
          diffuse: new pc.Color(0.7, 0.35, 0.1),
          emissive: new pc.Color(0.7, 0.35, 0.1),
          emissiveIntensity: 1.0,
        });
        for (let i = -1; i <= 1; i += 2) {
          const booster = new pc.Entity('booster');
          booster.addComponent('render', { type: 'cylinder' });
          booster.render!.meshInstances[0].material = boosterMat;
          booster.setLocalScale(0.22, 0.45, 0.22);
          booster.setLocalPosition(i * 0.45, -0.55, 0.65);
          base.addChild(booster);
        }
        break;
      }
      case 3: { // Spider legs
        const legMat = matCache.get({ diffuse: new pc.Color(0.3, 0.3, 0.35) });
        for (let i = 0; i < 4; i++) {
          const leg = new pc.Entity(`leg_${i}`);
          leg.addComponent('render', { type: 'box' });
          leg.render!.meshInstances[0].material = legMat;
          leg.setLocalScale(0.1, 0.1, 0.7);
          const angle = (i / 4) * Math.PI * 2 + Math.PI / 4;
          leg.setLocalPosition(Math.cos(angle) * 0.8, -0.55, Math.sin(angle) * 0.8);
          leg.setLocalEulerAngles(0, (angle * 180 / Math.PI), 0);
          base.addChild(leg);
        }
        break;
      }
    }
  }

  private buildNameTag(name: string) {
    // Use a simple floating entity - actual text rendered via CSS/HUD overlay
    // Store name for HUD to use
    this.root.name = `machine_${name}`;
  }

  private buildHpBar() {
    if (this.isLocal) return; // local player sees their HP in the HUD, not above the tank
    // HP bar floats above machine
    this.hpBar = new pc.Entity('hpBar');
    this.hpBar.addComponent('render', { type: 'plane' });
    this.hpBar.render!.meshInstances[0].material = matCache.get({ diffuse: new pc.Color(0.15, 0.05, 0.05) });
    this.hpBar.render!.castShadows = false;
    this.hpBar.render!.receiveShadows = false;
    this.hpBar.setLocalScale(1.4, 1, 0.14);
    this.hpBar.setLocalPosition(0, 1.8, 0);
    this.hpBar.setLocalEulerAngles(90, 0, 0);
    this.root.addChild(this.hpBar);

    this.hpBarFill = new pc.Entity('hpFill');
    this.hpBarFill.addComponent('render', { type: 'plane' });
    // createUnique: emissive is mutated every updateHpBar() call for color-shifting
    const fillMat = matCache.createUnique({
      diffuse: new pc.Color(0.1, 0.9, 0.3),
      emissive: new pc.Color(0.1, 0.9, 0.3),
      emissiveIntensity: 0.8,
    });
    this.hpBarFill.render!.meshInstances[0].material = fillMat;
    this.hpBarFill.render!.castShadows = false;
    this.hpBarFill.render!.receiveShadows = false;
    this.hpBarFill.setLocalScale(1, 1, 0.9);
    this.hpBarFill.setLocalPosition(0, 0.002, 0);
    this.hpBar.addChild(this.hpBarFill);

    // Remote machines start hidden; revealed on first hit via peekBullet / updateHpBar
    if (!this.isLocal) this.hpBar.enabled = false;
  }

  /**
   * Show the HP bar if a local bullet is within striking distance of this machine.
   * Call every frame inside a bullets.forEachActive loop for client-side prediction.
   * No-op for the local machine or once the bar is already visible.
   */
  peekBullet(bx: number, bz: number): void {
    if (this.isLocal || !this.hpBar || this.hpBar.enabled) return;
    const pos = this.root.getPosition();
    const dx = bx - pos.x;
    const dz = bz - pos.z;
    if (dx * dx + dz * dz < 1.2 * 1.2) this.hpBar.enabled = true;
  }

  /** Rotate the HP bar plane so it always faces the camera — call every frame. */
  billboard(cameraPos: pc.Vec3): void {
    if (!this.hpBar) return;
    const pos = this.root.getPosition();
    const dx = cameraPos.x - pos.x;
    const dz = cameraPos.z - pos.z;
    const yaw = Math.atan2(dx, dz) * (180 / Math.PI);
    this.hpBar.setLocalEulerAngles(90, yaw, 0);
  }

  updateHpBar(hp: number, maxHp: number) {
    if (!this.hpBarFill) return;
    const pct = Math.max(0, hp / maxHp);
    // Server confirmed damage — reveal bar if still hidden
    if (!this.isLocal && this.hpBar && !this.hpBar.enabled && hp < maxHp) {
      this.hpBar.enabled = true;
    }
    this.hpBarFill.setLocalScale(pct, 1, 0.9);
    this.hpBarFill.setLocalPosition((pct - 1) * 0.5, 0.002, 0);

    // Color shift green→yellow→red
    const mat = this.hpBarFill.render!.meshInstances[0].material as pc.StandardMaterial;
    if (pct > 0.5) {
      mat.emissive = new pc.Color(0.1, 0.9, 0.3);
    } else if (pct > 0.25) {
      mat.emissive = new pc.Color(0.9, 0.8, 0.1);
    } else {
      mat.emissive = new pc.Color(0.9, 0.1, 0.1);
    }
    mat.update();
  }

  setPosition(x: number, z: number) {
    this.root.setPosition(x, 0, z);
  }

  setBodyRotation(angle: number) {
    this._bodyRotationRad = angle;
    this.body.setLocalEulerAngles(0, angle * (180 / Math.PI), 0);
  }

  setTurretAngle(worldAngle: number) {
    // Compute local offset in radians to avoid getLocalEulerAngles() discontinuity at ±180°.
    const localRad = worldAngle - this._bodyRotationRad;
    this.turretBase.setLocalEulerAngles(0, localRad * (180 / Math.PI), 0);
  }

  /** World-space position of the primary barrel tip (barrel 0). */
  getMuzzlePosition(): pc.Vec3 {
    if (this.muzzleMarkers.length > 0) {
      return this.muzzleMarkers[0].getPosition().clone();
    }
    // Fallback — should not be reached after buildWeapon runs
    const pos = this.turretBase.getPosition().clone();
    const angle = this.turretBase.getLocalEulerAngles().y * (Math.PI / 180);
    pos.x += Math.sin(angle) * 0.9;
    pos.y += 0.1;
    pos.z -= Math.cos(angle) * 0.9;
    return pos;
  }

  /** World-space positions of ALL barrel tips (one per physical barrel). */
  getMuzzlePositions(): pc.Vec3[] {
    if (this.muzzleMarkers.length > 0) {
      return this.muzzleMarkers.map(m => m.getPosition().clone());
    }
    return [this.getMuzzlePosition()];
  }

  /** World-space position of the name tag anchor — above the HP bar. */
  getLabelAnchorPosition(): pc.Vec3 {
    const pos = this.root.getPosition().clone();
    pos.y += 2.5;
    return pos;
  }

  setFrozen(frozen: boolean) {
    if (this.frozenOverlay) this.frozenOverlay.enabled = frozen;
  }

  destroy() {
    this.root.destroy();
  }
}
